import React from 'react';
import * as THREE from '../../utils/three';

/**
 * Hero 内部背景动效：three.js points waves（透明叠加）
 * - 不改变父容器（Hero 卡片）的背景色
 * - canvas 绝对定位铺满，内容应放在其上层（z-index）
 */
export default function HeroWaves({
  className = '',
  opacity = 0.38,
  color = 0x60a5fa, // blue-400
  centerColor = 0xfff2a6, // 亮黄色（中心高光）
  centerIntensity = 0.9, // 0..1，中心高光强度
  size = 4.4,
  pointSizeFactor = 320,
  cover = 1.18,
  density = 1
}) {
  const canvasRef = React.useRef(null);
  const rafRef = React.useRef(0);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // 尊重“减少动态效果”
    const mqReduce =
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
    if (mqReduce?.matches) return;

    let renderer = null;
    let scene = null;
    let camera = null;
    let points = null;
    let geometry = null;
    let material = null;

    let width = 0;
    let height = 0;
    let mouseX = 0;
    let mouseY = 0;
    let count = 0;

    // 粒子密度：按容器大小自适应（保持 points-waves “地面波场”的本来观感）
    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
    const computeGrid = (w, h) => {
      // 经验值：Hero 矮屏更需要“深度（Z）”来铺满视觉
      const area = Math.max(1, w * h);
      const base = clamp(Math.round(Math.sqrt(area) / 28), 26, 44); // world separation
      // density 代表“总点数倍率”，所以 x/y 维度按 sqrt(density) 缩放
      const d = clamp(Number(density) || 1, 0.08, 2);
      const s = Math.sqrt(d);
      const amountX = clamp(Math.round((Math.round(w / 9) + 60) * s), 60, 220);
      const amountY = clamp(Math.round((Math.round(h / 5) + 40) * s), 48, 200);
      return { amountX, amountY, separation: base };
    };

    const state = { amountX: 120, amountY: 120, separation: 34 };
    const getParent = () => canvas.parentElement || canvas;

    const onPointerMove = (e) => {
      const el = getParent();
      const r = el.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      mouseX = (x - r.width / 2) * 0.55;
      mouseY = (y - r.height / 2) * 0.55;
    };

    const initScene = () => {
      const el = getParent();
      const r = el.getBoundingClientRect();
      width = Math.max(1, Math.floor(r.width));
      height = Math.max(1, Math.floor(r.height));

      const g = computeGrid(width, height);
      state.amountX = g.amountX;
      state.amountY = g.amountY;
      state.separation = g.separation;

      scene = new THREE.Scene();
      const fov = 62;
      camera = new THREE.PerspectiveCamera(fov, width / height, 1, 10000);

      // points-waves：点阵在 x/z 平面，y 方向波动
      const planeW = (state.amountX - 1) * state.separation;
      const planeD = (state.amountY - 1) * state.separation;

      // 相机距离：按更长边估算，让波场覆盖 Hero 外框（略做 cover 放大）
      const vFov = THREE.MathUtils.degToRad(fov);
      const aspect = width / height;
      const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
      const distW = (planeW / 2) / Math.tan(hFov / 2);
      const distD = (planeD / 2) / Math.tan(vFov / 2);
      const dist = Math.max(distW, distD) / Math.max(0.6, cover);

      camera.position.set(0, 220, dist * 1.08);

      geometry = new THREE.BufferGeometry();
      const num = state.amountX * state.amountY;
      const positions = new Float32Array(num * 3);
      const scales = new Float32Array(num);

      let i = 0;
      for (let ix = 0; ix < state.amountX; ix++) {
        for (let iy = 0; iy < state.amountY; iy++) {
          positions[i * 3] = ix * state.separation - planeW / 2; // x
          positions[i * 3 + 1] = 0; // y
          positions[i * 3 + 2] = iy * state.separation - planeD / 2; // z
          scales[i] = 1;
          i++;
        }
      }

      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('scale', new THREE.BufferAttribute(scales, 1));

      // 用 shader 才能真正使用 attribute scale（Three.js 官方 points-waves 示例同款做法）
      material = new THREE.ShaderMaterial({
        uniforms: {
          uColor: { value: new THREE.Color(color) },
          uCenterColor: { value: new THREE.Color(centerColor) },
          uCenterIntensity: { value: centerIntensity },
          uOpacity: { value: opacity },
          uSizeFactor: { value: pointSizeFactor },
          uBaseSize: { value: size }
        },
        vertexShader: `
          attribute float scale;
          uniform float uSizeFactor;
          uniform float uBaseSize;
          void main() {
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            gl_PointSize = (uBaseSize * scale) * (uSizeFactor / -mvPosition.z);
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          uniform vec3 uColor;
          uniform vec3 uCenterColor;
          uniform float uCenterIntensity;
          uniform float uOpacity;
          void main() {
            // 七角星点 + 边缘柔化
            // gl_PointCoord: [0,1]，先转换到 [-1,1]，再用极坐标做 7 个尖角的半径函数
            vec2 p = gl_PointCoord * 2.0 - 1.0;
            float d = length(p);
            float theta = atan(p.y, p.x); // [-pi, pi]

            // 0..1 的“尖角强度”，通过幂函数让尖角更尖
            float t = 0.5 + 0.5 * cos(7.0 * theta);
            float spike = pow(t, 10.0);

            // 七角星的边界半径：中心更厚、尖角更长
            float rEdge = mix(0.35, 0.95, spike);
            if (d > rEdge) discard;

            // 羽化边缘，避免硬边锯齿
            float feather = 0.08;
            float a = smoothstep(rEdge, rEdge - feather, d) * uOpacity;

            // 中心高光：越靠中心越偏亮黄色（并略微提亮中心）
            float inner = 1.0 - clamp(d / max(0.0001, rEdge), 0.0, 1.0);
            inner = pow(inner, 1.8); // 收紧到中心
            float k = clamp(inner * uCenterIntensity, 0.0, 1.0);
            vec3 col = mix(uColor, uCenterColor, k);
            col *= (1.0 + 0.35 * k);

            gl_FragColor = vec4(col, a);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending
      });

      points = new THREE.Points(geometry, material);
      // 轻微倾斜，让矮 Hero 中也能看到“整块波场”，但仍然保持 points-waves 的观感
      points.rotation.x = -Math.PI * 0.22;
      scene.add(points);

      renderer = new THREE.WebGLRenderer({
        canvas,
        alpha: true,
        antialias: true,
        powerPreference: 'high-performance'
      });
      renderer.setClearColor(0x000000, 0);
      renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
      renderer.setSize(width, height, false);

      // 让动效跟随容器鼠标，但不挡交互（canvas pointer-events: none）
      window.addEventListener('pointermove', onPointerMove, { passive: true });
    };

    const renderFrame = () => {
      if (!renderer || !scene || !camera || !geometry) return;

      // camera 缓动跟随
      camera.position.x += (mouseX - camera.position.x) * 0.04;
      camera.position.y += (220 + -mouseY - camera.position.y) * 0.04;
      camera.lookAt(scene.position);

      const posAttr = geometry.getAttribute('position');
      const scaleAttr = geometry.getAttribute('scale');

      let i = 0;
      for (let ix = 0; ix < state.amountX; ix++) {
        for (let iy = 0; iy < state.amountY; iy++) {
          const y =
            Math.sin((ix + count) * 0.34) * 24 +
            Math.sin((iy + count) * 0.48) * 24;
          posAttr.setY(i, y);
          scaleAttr.setX(
            i,
            (Math.sin((ix + count) * 0.3) + 1.0) * 1.6 +
              (Math.sin((iy + count) * 0.5) + 1.0) * 1.2 +
              0.6
          );
          i++;
        }
      }
      posAttr.needsUpdate = true;
      scaleAttr.needsUpdate = true;

      renderer.render(scene, camera);
      count += 0.06;
      rafRef.current = window.requestAnimationFrame(renderFrame);
    };

    const disposeScene = () => {
      try {
        if (scene && points) scene.remove(points);
      } catch (_) {}
      try {
        geometry?.dispose?.();
      } catch (_) {}
      try {
        material?.dispose?.();
      } catch (_) {}
      try {
        renderer?.dispose?.();
      } catch (_) {}
      scene = null;
      camera = null;
      points = null;
      geometry = null;
      material = null;
      renderer = null;
    };

    const onResize = () => {
      const el = getParent();
      const r = el.getBoundingClientRect();
      const w = Math.max(1, Math.floor(r.width));
      const h = Math.max(1, Math.floor(r.height));
      if (w === width && h === height) return;
      width = w;
      height = h;
      // 尺寸变更时重建网格，让粒子永远以 Hero 外框为边界铺满
      disposeScene();
      try {
        initScene();
      } catch (_) {}
    };

    try {
      initScene();
      rafRef.current = window.requestAnimationFrame(renderFrame);
      window.addEventListener('resize', onResize);
    } catch (e) {
      // WebGL 不可用/初始化失败：安静降级，不影响页面
      try {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('resize', onResize);
      } catch (_) {}
    }

    return () => {
      try {
        window.cancelAnimationFrame(rafRef.current);
      } catch (_) {}
      try {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('resize', onResize);
      } catch (_) {}

      try {
        if (scene && points) scene.remove(points);
      } catch (_) {}
      try {
        geometry?.dispose?.();
      } catch (_) {}
      try {
        material?.dispose?.();
      } catch (_) {}
      try {
        renderer?.dispose?.();
      } catch (_) {}
    };
  }, [color, centerColor, centerIntensity, opacity, size, pointSizeFactor, cover, density]);

  return (
    <canvas
      ref={canvasRef}
      className={[
        'absolute inset-0 w-full h-full pointer-events-none',
        className
      ].join(' ')}
    />
  );
}

